import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import { eventService } from '../../../services/api';
import Pagination from '../../common/Pagination';
import EventsGrid from '../../events/EventsGrid';
import { getEventUrl } from '../../../utils/urlHelpers';
import SocietySectionToolbar from './SocietySectionToolbar';
import SectionFilterSelect from './SectionFilterSelect';

const SocietyEvents = ({ society, onError }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    date_range: 'all',
    sort: 'upcoming'
  });

  // Load events
  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        const activeFilters = Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v && v !== 'all')
        );
        const response = await eventService.getEvents({
          society: society.id,
          page: currentPage,
          page_size: 10,
          ...activeFilters
        });
        
        setEvents(response.results || response);
        setTotalPages(response.total_pages || 1);
        setTotalCount(response.count || (response.results ? response.results.length : 0));
      } catch (error) {
        console.error('Error loading events:', error);
        onError('Failed to load events');
        setEvents([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [society.id, currentPage, filters, onError]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      community: 'bg-blue-100 text-blue-800',
      celebration: 'bg-green-100 text-green-800',
      meeting: 'bg-purple-100 text-purple-800',
      sports: 'bg-orange-100 text-orange-800',
      education: 'bg-yellow-100 text-yellow-800',
      health: 'bg-red-100 text-red-800'
    };
    return colors[category] || colors.community;
  };

  const isEventUpcoming = (event) => {
    return new Date(event.created_at) > new Date();
  };

  const isEventToday = (event) => {
    const today = new Date();
    const eventDate = new Date(event.created_at);
    return today.toDateString() === eventDate.toDateString();
  };

  const getEventStatus = (event) => {
    if (isEventToday(event)) return 'today';
    if (isEventUpcoming(event)) return 'upcoming';
    return 'past';
  };

  const activeFilterCount =
    (filters.category !== 'all' ? 1 : 0) +
    (filters.date_range !== 'all' ? 1 : 0) +
    (filters.sort !== 'upcoming' ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <SocietySectionToolbar
        title="Events"
        count={totalCount}
        itemLabel={{ singular: 'event', plural: 'events' }}
        societyName={society.name}
        searchPlaceholder="Search events..."
        searchValue={filters.search}
        onSearchChange={(v) => handleFilterChange({ ...filters, search: v })}
        activeFilterCount={activeFilterCount}
        addButton={
          isAuthenticated
            ? {
                label: 'Create Event',
                onClick: () =>
                  navigate(`/societies/${society.slug}/events/create?society=${society.id}`),
              }
            : null
        }
      >
        <SectionFilterSelect
          label="Category"
          value={filters.category}
          onChange={(e) => handleFilterChange({ ...filters, category: e.target.value })}
        >
          <option value="all">All Categories</option>
          <option value="community">Community</option>
          <option value="celebration">Celebration</option>
          <option value="meeting">Meeting</option>
          <option value="sports">Sports</option>
          <option value="education">Education</option>
          <option value="health">Health</option>
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Date Range"
          value={filters.date_range}
          onChange={(e) => handleFilterChange({ ...filters, date_range: e.target.value })}
        >
          <option value="all">All Events</option>
          <option value="upcoming">Upcoming</option>
          <option value="today">Today</option>
          <option value="this_week">This Week</option>
          <option value="this_month">This Month</option>
          <option value="past">Past Events</option>
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Sort By"
          value={filters.sort}
          onChange={(e) => handleFilterChange({ ...filters, sort: e.target.value })}
        >
          <option value="upcoming">Upcoming First</option>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="title">Title A-Z</option>
        </SectionFilterSelect>
      </SocietySectionToolbar>

      {/* Events Grid */}
      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="medium" />
        </div>
      ) : (
        <EventsGrid 
          events={events} 
          societySlug={society.slug}
          onEventClick={(event) => navigate(getEventUrl(event, society.slug))}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default SocietyEvents;

